'use client';

import { ChevronRight, Star } from 'lucide-react';
import { LucideIcon } from '@/src/components/LucideIcon';
import { getReviewsEntryVariant } from './reviewsAvailability';
import { ReviewsSourceIcon } from './ReviewsSourceIcon';
import * as styles from './reviews.styles';

export type AggregateReviewsRating = {
  rating: number;
  reviewCount: number;
};

type ReviewsEntryPointProps = {
  hasReputation?: boolean;
  onEntryClick?: () => void;
  aggregateRating: AggregateReviewsRating | null;
  hasGooglePlace: boolean;
  hasRedditLinks: boolean;
  targetId: string;
};

const numberFormatter = new Intl.NumberFormat('en-US');

const getAccessibleLabel = ({
  aggregateRating,
  hasGooglePlace,
  hasRedditLinks,
  hasReputation,
}: Omit<ReviewsEntryPointProps, 'targetId'>): string => {
  const sourceLabels: Array<string> = [];
  if (aggregateRating) {
    sourceLabels.push(
      `${aggregateRating.rating.toFixed(1)} rating from ${numberFormatter.format(aggregateRating.reviewCount)} web reviews`,
    );
  }
  if (!aggregateRating && hasReputation) sourceLabels.push('Web reviews');
  if (hasGooglePlace) sourceLabels.push('Google reviews');
  if (hasRedditLinks) sourceLabels.push('Reddit discussions');

  if (sourceLabels.length === 1) return `View ${sourceLabels[0]}`;
  return `View ${sourceLabels.slice(0, -1).join(', ')}, and ${sourceLabels.at(-1)}`;
};

export function ReviewsEntryPoint({
  aggregateRating,
  hasGooglePlace,
  hasRedditLinks,
  targetId,
  hasReputation = aggregateRating !== null,
  onEntryClick,
}: ReviewsEntryPointProps) {
  const variant = getReviewsEntryVariant({
    hasReputation,
    hasGooglePlace,
    hasRedditLinks,
  });

  if (!variant) return null;

  const hasAggregate = aggregateRating !== null;
  const showsMultipleProviderTypes = hasAggregate && (hasGooglePlace || hasRedditLinks);
  const rating = aggregateRating?.rating.toFixed(1);
  const count = aggregateRating ? numberFormatter.format(aggregateRating.reviewCount) : null;
  const descriptor = (() => {
    if (variant === 'web-google-reddit') return 'reviews & discussions';
    if (variant === 'web-google') return 'reviews';
    if (variant === 'web-reddit') return 'discussions';
    if (variant === 'web') return count === null ? 'Reviews across the web' : `${count} reviews across the web`;
    if (variant === 'google-reddit') return 'Reviews & discussions about this place';
    if (variant === 'google') return 'Reviews about this place';
    return 'Discussions about this place';
  })();
  const emphasizedWord = !hasAggregate && (variant === 'google' || variant === 'reddit');

  return (
    <a
      className={styles.entryPoint}
      href={`#${targetId}`}
      onClick={(event) => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        requestAnimationFrame(() => document.getElementById(`${targetId}-title`)?.focus({ preventScroll: true }));
        onEntryClick?.();
      }}
      aria-label={getAccessibleLabel({ aggregateRating, hasGooglePlace, hasRedditLinks, hasReputation })}
    >
      <span className={styles.entryPointContent}>
        {hasAggregate && (
          <span className={styles.ratingGroup}>
            <LucideIcon icon={Star} size="sm" fill="currentColor" className={styles.ratingStar} />
            <span className={styles.ratingValue}>{rating}</span>
            {showsMultipleProviderTypes && <span className={styles.reviewCount}>({count})</span>}
          </span>
        )}

        {showsMultipleProviderTypes && <span className={styles.sourceDivider} aria-hidden="true" />}

        {(hasGooglePlace || hasRedditLinks) && (
          <span className={styles.sourceMarks} aria-hidden="true">
            {hasGooglePlace && <ReviewsSourceIcon source="google" />}
            {hasRedditLinks && <ReviewsSourceIcon source="reddit" />}
          </span>
        )}

        <span className={emphasizedWord ? styles.entryPointLabelStrong : styles.entryPointLabel}>{descriptor}</span>
      </span>
      <LucideIcon icon={ChevronRight} size="sm" className={styles.entryPointChevron} />
    </a>
  );
}
